// src/components/adoption/AdoptionPostFeed.jsx
"use client";

import {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { getUserTypeColor } from "../../utils/userTypeUtils";
import { adoptionAPI } from "../../services/adoptionAPI";
import { handleApiError, petAPI } from "../../services/api";
import {
  showSuccessToast,
  showErrorToast,
} from "../../utils/notificationUtils";
import { normalizePetInfo } from "../../utils/petDataUtils";

import AdoptionPost from "./AdoptionPost";
import "./AdoptionPostFeed.css";

const AdoptionPostFeed = forwardRef(
  (
    {
      currentUser,
      refreshTrigger = 0,
      onEditPost,
      onViewRequests,
      onOfferForAdoption,
      highlightedPostId,
      onViewDetails,
    },
    ref
  ) => {
    const { t, i18n } = useTranslation();
    const { getUserType } = useAuth();
    
    // Get user type color
    const userType = getUserType();
    const userTypeColor = getUserTypeColor(userType);
    
    // Helper function to convert hex to rgba
    const hexToRgba = (hex, alpha) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    
    // Calculate color variants for CSS variables
    const colorVariants = {
      main: userTypeColor,
      shadow15: hexToRgba(userTypeColor, 0.15),
      shadow25: hexToRgba(userTypeColor, 0.25),
      shadow35: hexToRgba(userTypeColor, 0.35),
      bg10: hexToRgba(userTypeColor, 0.1),
    };

    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMorePosts, setHasMorePosts] = useState(true);
    const [filter, setFilter] = useState("all"); // 'all' | 'mine'
    const [activeHighlightId, setActiveHighlightId] = useState(null);
    const [attributeFilters, setAttributeFilters] = useState({
      city: "any",
      ageGroup: "any",
      species: "any",
      gender: "any",
    });
    const [cityOptions, setCityOptions] = useState([]);
    const [speciesOptions, setSpeciesOptions] = useState([]);
    const mountedRef = useRef(true);
    const isLoadingRef = useRef(false);

    const isRTL = i18n.dir() === "rtl";

    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
      };
    }, []);

    // Load species once so the Species filter can send species_id to the server
    useEffect(() => {
      petAPI
        .getPetsBySpecies(null, { dropdown: true })
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : res.data.data || [];
          const options = list
            .filter((s) => s && s.id != null)
            .map((s) => ({
              value: String(s.id),
              label: isRTL
                ? s.name_ar || s.name_en || s.name
                : s.name_en || s.name_ar || s.name,
            }))
            .sort((a, b) =>
              a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
            );
          if (mountedRef.current) setSpeciesOptions(options);
        })
        .catch((err) => {
          console.error("Error fetching species for adoption filter:", err);
          if (mountedRef.current) setSpeciesOptions([]);
        });
    }, [isRTL]);

    // Load adoption posts
    // Note: Backend automatically filters posts by the authenticated user's country
    // (same logic as social feed consolidated-feed endpoint)
    const loadAdoptionPosts = async (page = 1, append = false) => {
      if (isLoadingRef.current || !mountedRef.current) return;

      try {
        isLoadingRef.current = true;
        setError(null);

        if (!append && mountedRef.current) {
          setLoading(true);
        }

        const params = { page, per_page: 10 };

        // Server-side attribute filters (sent only when a value is selected)
        if (attributeFilters.city !== "any") params.city_id = attributeFilters.city;
        if (attributeFilters.species !== "any") params.species_id = attributeFilters.species;
        if (attributeFilters.gender !== "any") params.gender = attributeFilters.gender;
        if (attributeFilters.ageGroup !== "any") params.age_group = attributeFilters.ageGroup;

        const response = await adoptionAPI.getAdoptionPosts(params);

        if (!mountedRef.current) return;

        let postsData = [];
        if (response.data) {
          if (response.data.status === 'success' && response.data.data) {
            if (response.data.data.data && Array.isArray(response.data.data.data)) {
              postsData = response.data.data.data;
            } else if (Array.isArray(response.data.data)) {
              postsData = response.data.data;
            }
          } else if (Array.isArray(response.data)) {
            postsData = response.data;
          }
        }

        // Build the city filter options from the same response; backend returns the
        // distinct cities that currently have adoption posts in the viewer's country.
        const availableCities = response.data?.data?.available_cities;
        if (Array.isArray(availableCities)) {
          const byId = new Map();
          availableCities.forEach((city) => {
            const id = city?.id ?? city?.city_id ?? city?.cityId;
            const label = isRTL
              ? city?.name_ar || city?.name_en || city?.name
              : city?.name_en || city?.name_ar || city?.name;
            if (id == null || typeof label !== "string" || !label.trim()) return;
            if (!byId.has(id)) byId.set(id, label);
          });

          const uniqueSorted = Array.from(byId.entries())
            .map(([id, label]) => ({ value: String(id), label }))
            .sort((a, b) =>
              a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
            );

          if (mountedRef.current) setCityOptions(uniqueSorted);
        }

        // Transform posts to match expected structure
        const transformedPosts = postsData.map(post => {
          // Handle pet_info - could be single object, array, or nested in pets array
          let petInfo = post.pet_info;
          if (!petInfo && post.pets && Array.isArray(post.pets)) {
            petInfo = post.pets;
          } else if (!petInfo && post.pet) {
            petInfo = post.pet;
          }

          // Normalize pet_info using utility function
          const normalizedPetInfo = normalizePetInfo(petInfo);

          return {
            id: post.id,
            pet_id: post.pet_id || post.pet_ids,
            author: {
              id: post.author?.id || post.user_id || post.user?.id,
              name:
                post.author?.name ||
                (post.author?.first_name && post.author?.last_name
                  ? `${post.author.first_name} ${post.author.last_name}`.trim()
                  : post.author?.first_name || post.author?.name || "Unknown"),
              username: post.author?.username || post.author?.name || "@unknown",
              avatar: post.author?.avatar || post.author?.profile_image_url,
              connection_status:
                post.author?.connection_status || post.connection_status || "none",
              // New: carry over guardian/vet subtype so we can show \"Rescuer\" etc.
              sub_type: post.author?.sub_type || post.author?.subType,
              sub_type_display_name:
                post.author?.sub_type_display_name ||
                post.author?.subTypeDisplayName,
            },
            pet_info: normalizedPetInfo,
            about_pet: post.about_pet || post.about_pet_text || '',
            looking_for: post.looking_for || post.looking_for_text || '',
            status: post.status || 'offered',
            created_at: post.created_at || post.createdAt,
            adoption_requests_count: post.adoption_requests_count || 0,
            additional_images: post.additional_images || [],
            vaccinated: typeof post.vaccinated !== 'undefined' ? post.vaccinated : null,
            my_request_status: post.my_request_status || null, // Include user's request status
            has_shared: post.has_shared || false, // Include share status
          };
        });

        if (append) {
          setPosts(prev => [...prev, ...transformedPosts]);
        } else {
          setPosts(transformedPosts);
        }

        // Check if there are more pages
        const totalPages = response.data?.meta?.last_page ||
          response.data?.last_page ||
          response.data?.data?.last_page || 1;
        setHasMorePosts(page < totalPages);

        setCurrentPage(page);
      } catch (err) {
        if (mountedRef.current) {
          setError(handleApiError(err));
        }
      } finally {
        if (mountedRef.current) {
          isLoadingRef.current = false;
          setLoading(false);
        }
      }
    };

    // Initial load, refresh on trigger change, and refetch (from page 1)
    // whenever a server-side attribute filter changes
    useEffect(() => {
      loadAdoptionPosts(1, false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTrigger, attributeFilters]);

    // Apply inline highlight when parent passes a highlighted post id
    useEffect(() => {
      if (!highlightedPostId) return;

      setActiveHighlightId(highlightedPostId);

      const timeout = setTimeout(() => {
        setActiveHighlightId(null);
      }, 1800);

      return () => clearTimeout(timeout);
    }, [highlightedPostId]);

    // Load more posts
    const loadMorePosts = () => {
      if (hasMorePosts && !loading && !isLoadingRef.current) {
        loadAdoptionPosts(currentPage + 1, true);
      }
    };

    // Infinite scroll
    useEffect(() => {
      if (!hasMorePosts || loading || isLoadingRef.current) {
        return;
      }

      const handleScroll = () => {
        const scrollPosition = window.innerHeight + window.scrollY;
        const documentHeight = document.documentElement.scrollHeight;

        if (documentHeight - scrollPosition < 200 && posts.length >= 10) {
          loadMorePosts();
        }
      };

      let ticking = false;
      const throttledHandleScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            handleScroll();
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('scroll', throttledHandleScroll, { passive: true });

      return () => {
        window.removeEventListener('scroll', throttledHandleScroll);
      };
    }, [hasMorePosts, loading, posts.length, currentPage]);

    const handleOfferToAdopt = async (postId) => {
      try {
        // Optimistically update the post to show "Pending" immediately
        setPosts(prevPosts =>
          prevPosts.map(post =>
            post.id === postId
              ? { ...post, my_request_status: 'pending' }
              : post
          )
        );

        await adoptionAPI.sendAdoptionRequest(postId);
        showSuccessToast(t('adoption.postFeed.offerSuccess') || 'Your offer to adopt has been sent successfully!');

        // Refresh to get the latest data from backend
        loadAdoptionPosts(1, false);
      } catch (err) {
        // Revert the optimistic update on error
        setPosts(prevPosts =>
          prevPosts.map(post =>
            post.id === postId
              ? { ...post, my_request_status: null }
              : post
          )
        );
        showErrorToast(handleApiError(err) || t('adoption.postFeed.offerError') || 'Failed to send adoption offer');
        throw err;
      }
    };

    const handleEditPost = (post) => {
      if (onEditPost) {
        onEditPost(post);
      }
    };

    const handleDeletePost = async (postId) => {
      try {
        await adoptionAPI.deleteAdoptionPost(postId);
        showSuccessToast(t('adoption.postFeed.deleteSuccess') || 'Adoption post deleted successfully');
        loadAdoptionPosts(1, false);
      } catch (err) {
        showErrorToast(handleApiError(err) || t('adoption.postFeed.deleteError') || 'Failed to delete adoption post');
      }
    };

    // Expose method to update request count when a request is rejected
    useImperativeHandle(ref, () => ({
      decrementRequestCount: () => {
        // Decrement adoption_requests_count for all posts (since requests shown are for user's posts)
        setPosts(prev => prev.map(post => ({
          ...post,
          adoption_requests_count: Math.max(0, (post.adoption_requests_count || 0) - 1)
        })));
      }
    }), []);

    // --- Filter metadata (server-side filtering) ---
    const AGE_GROUP_LABELS = {
      baby: t('adoption.filters.ageGroups.baby') || 'Baby (< 1 year)',
      young: t('adoption.filters.ageGroups.young') || 'Young (1-3 years)',
      adult: t('adoption.filters.ageGroups.adult') || 'Adult (3-7 years)',
      senior: t('adoption.filters.ageGroups.senior') || 'Senior (7+ years)',
    };

    const GENDER_LABELS = {
      male: t('adoption.filters.genders.male') || 'Male',
      female: t('adoption.filters.genders.female') || 'Female',
      other: t('adoption.filters.genders.other') || 'Other',
    };

    // Apply client-side filter for "All posts" vs "My posts"
    const filteredPostsByOwner = posts.filter((post) => {
      const status = (post.status || '').toLowerCase();

      // In the global feed, hide adopted posts so only active offers show
      if (filter === 'all') {
        return status !== 'adopted';
      }

      // In "My posts", show all of the current user's posts, including adopted ones
      if (filter === 'mine' && currentUser?.id) {
        return post.author?.id === currentUser.id;
      }

      return true;
    });

    const handleAttributeFilterChange = (key, value) => {
      setAttributeFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    };

    const handleClearAttributeFilters = () => {
      setAttributeFilters({
        city: 'any',
        ageGroup: 'any',
        species: 'any',
        gender: 'any',
      });
    };

    const hasActiveAttributeFilters = useMemo(() => {
      return Object.values(attributeFilters).some((val) => val !== 'any');
    }, [attributeFilters]);

    if (loading && posts.length === 0) {
      return (
        <div className="adoption-post-feed-loading">
          <div className="adoption-post-feed-spinner"></div>
          <p>{t('adoption.feed.loading') || 'Loading adoption posts...'}</p>
        </div>
      );
    }

    if (error && posts.length === 0) {
      return (
        <div className="adoption-post-feed-error">
          <p>{error}</p>
          <button onClick={() => loadAdoptionPosts(1, false)}>
            {t('adoption.feed.retry') || 'Retry'}
          </button>
        </div>
      );
    }

    // Attribute-based filtering (city, age, species, gender) is applied
    // server-side; `posts` already reflects the active attributeFilters.
    const showBaseEmpty = !loading && posts.length === 0 && !hasActiveAttributeFilters;
    const showFilteredEmpty = !loading && filteredPostsByOwner.length === 0 && !showBaseEmpty;

    return (
      <div 
        className="adoption-post-feed"
        style={{
          '--adoption-feed-user-type-color': colorVariants.main,
          '--adoption-feed-user-type-shadow-15': colorVariants.shadow15,
          '--adoption-feed-user-type-shadow-25': colorVariants.shadow25,
          '--adoption-feed-user-type-shadow-35': colorVariants.shadow35,
          '--adoption-feed-user-type-bg-10': colorVariants.bg10,
        }}
      >
        <div className="adoption-post-feed-toolbar">
          <div className="adoption-post-feed-filters">
            <button
              type="button"
              className={`adoption-feed-chip ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              {t('adoption.feed.filterAll') || 'All posts'}
            </button>
            <button
              type="button"
              className={`adoption-feed-chip ${filter === 'mine' ? 'active' : ''}`}
              onClick={() => setFilter('mine')}
              disabled={!currentUser?.id}
            >
              {t('adoption.feed.filterMine') || 'My posts'}
            </button>
          </div>
        </div>

        {!loading && (
          <div className={`adoption-post-feed-main ${isRTL ? 'adoption-post-feed-main-rtl' : ''}`}>
            {/* Filters sidebar */}
            <aside className="adoption-filters-sidebar">
              <div className="adoption-filters-header">
                <h3 className="adoption-filters-title">
                  {t('adoption.filters.title') || 'Filters'}
                </h3>
                {hasActiveAttributeFilters && (
                  <button
                    type="button"
                    className="adoption-filters-clear"
                    onClick={handleClearAttributeFilters}
                  >
                    {t('adoption.filters.clearAll') || 'Clear all'}
                  </button>
                )}
              </div>

              <div className="adoption-filter-group">
                <label className="adoption-filter-label">
                  {t('adoption.filters.city') || 'City'}
                </label>
                <select
                  className="adoption-filter-select"
                  value={attributeFilters.city}
                  onChange={(e) =>
                    handleAttributeFilterChange('city', e.target.value)
                  }
                >
                  <option value="any">
                    {t('adoption.filters.any') || 'Any'}
                  </option>
                  {cityOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="adoption-filter-group">
                <label className="adoption-filter-label">
                  {t('adoption.filters.age') || 'Age'}
                </label>
                <select
                  className="adoption-filter-select"
                  value={attributeFilters.ageGroup}
                  onChange={(e) =>
                    handleAttributeFilterChange('ageGroup', e.target.value)
                  }
                >
                  <option value="any">
                    {t('adoption.filters.any') || 'Any'}
                  </option>
                  {Object.entries(AGE_GROUP_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="adoption-filter-group">
                <label className="adoption-filter-label">
                  {t('adoption.filters.species') || 'Species'}
                </label>
                <select
                  className="adoption-filter-select"
                  value={attributeFilters.species}
                  onChange={(e) =>
                    handleAttributeFilterChange('species', e.target.value)
                  }
                >
                  <option value="any">
                    {t('adoption.filters.any') || 'Any'}
                  </option>
                  {speciesOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="adoption-filter-group">
                <label className="adoption-filter-label">
                  {t('adoption.filters.gender') || 'Gender'}
                </label>
                <select
                  className="adoption-filter-select"
                  value={attributeFilters.gender}
                  onChange={(e) =>
                    handleAttributeFilterChange('gender', e.target.value)
                  }
                >
                  <option value="any">
                    {t('adoption.filters.any') || 'Any'}
                  </option>
                  {Object.entries(GENDER_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </aside>

            {/* Results column */}
            <section className="adoption-results-column">
              {showBaseEmpty ? (
                <div className="adoption-post-feed-empty adoption-post-feed-empty-inline">
                  <img
                    src="/icons/adoptionnoposts.png"
                    alt={t('adoption.feed.emptyImageAlt') || 'No adoption posts illustration'}
                    className="adoption-post-feed-empty-image"
                  />
                  <h3>{t('adoption.feed.emptyTitle') || 'No adoption posts'}</h3>
                  <p>{t('adoption.feed.empty') || 'No adoption posts available yet.'}</p>
                  {typeof onOfferForAdoption === 'function' && (
                    <button
                      type="button"
                      className="adoption-post-feed-empty-cta"
                      onClick={onOfferForAdoption}
                    >
                      {t('adoption.feed.emptyCta') || 'Offer a pet for adoption'}
                    </button>
                  )}
                </div>
              ) : showFilteredEmpty ? (
                <div className="adoption-post-feed-empty adoption-post-feed-empty-inline">
                  <img
                    src="/icons/adoptionnoposts.png"
                    alt={
                      t('adoption.feed.emptyImageAlt') ||
                      'No adoption posts illustration'
                    }
                    className="adoption-post-feed-empty-image"
                  />
                  <h3>
                    {t('adoption.feed.emptyFilteredTitle') ||
                      'No adoption posts'}
                  </h3>
                  <p>
                    {t('adoption.feed.emptyFiltered') ||
                      'No posts match your current filters.'}
                  </p>
                </div>
              ) : (
                <div className="adoption-post-grid">
                  {filteredPostsByOwner.map((post, index) => (
                    <AdoptionPost
                      key={post.id}
                      post={post}
                      currentUser={currentUser}
                      onOfferToAdopt={handleOfferToAdopt}
                      onEdit={handleEditPost}
                      onDelete={handleDeletePost}
                      onViewRequests={onViewRequests}
                      onViewDetails={onViewDetails}
                      isHighlighted={
                        !!activeHighlightId &&
                        (activeHighlightId === post.id ||
                          (activeHighlightId === 'newest' && index === 0))
                      }
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {hasMorePosts && !showBaseEmpty && !showFilteredEmpty && (
          <div className="adoption-post-feed-load-more">
            <button
              onClick={loadMorePosts}
              disabled={loading || isLoadingRef.current}
              className="adoption-post-feed-load-more-btn"
            >
              {loading ? (
                <>
                  <div className="adoption-post-feed-spinner"></div>
                  {t('adoption.feed.loadingMore') || 'Loading...'}
                </>
              ) : (
                t('adoption.feed.loadMore') || 'Load More'
              )}
            </button>
          </div>
        )}
      </div>
    );
  });

AdoptionPostFeed.displayName = 'AdoptionPostFeed';

export default AdoptionPostFeed;

